import { createContext, useContext, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "@/data/copy";
import {
  DEFAULT_LANG,
  langFromPath,
  rememberLang,
  stripLang,
  withLang,
} from "@/lib/lang";

const LanguageContext = createContext(null);

/**
 * Language is derived from the URL (/ar/..., /en/...) rather than stored state,
 * so every translation has its own indexable address. localStorage only records
 * the visitor's preference for the next time they land on the bare domain.
 */
export function LanguageProvider({ children }) {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  const lang = langFromPath(pathname) || DEFAULT_LANG;
  const isRtl = lang === "ar";
  const dir = isRtl ? "rtl" : "ltr";

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("lang", lang);
    html.setAttribute("dir", dir);
    rememberLang(lang);
  }, [lang, dir]);

  // Switch language while staying on the same page, preserving query and hash.
  const toggle = useCallback(() => {
    const next = lang === "en" ? "ar" : "en";
    rememberLang(next);
    navigate(`${withLang(next, stripLang(pathname))}${search}${hash}`);
  }, [lang, navigate, pathname, search, hash]);

  const value = useMemo(
    () => ({ lang, toggle, isRtl, dir, t: T[lang] }),
    [lang, toggle, isRtl, dir]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}

// pick localized value from a {en, ar} object
export function useL() {
  const { lang } = useLang();
  return (obj) => (obj && typeof obj === "object" && "en" in obj ? obj[lang] : obj);
}
